/**
 * Auth modal orchestration — login / register / reset-password.
 *
 * Port-shape of legacy `amri.login.js`, simplified for the v2 starting set:
 *   - Click `[data-modal-toggle="<id>"]` → open the modal with id `amri-modal-<id>`.
 *   - Click `[data-modal-control="close"]` (X icon or cover overlay) → close.
 *   - ESC key → close any open modal.
 *   - Mode auto-shows on first paint if `?showLoginModal=1` query param is present
 *     (used by Surface Controller error path to re-open the modal after a failed
 *     login post) OR if `?signup=true` is present (used by the /register/
 *     marketing landing to open the modal when a legacy backlink lands anonymous).
 *
 * Form submission uses native browser POST — the AccountSurfaceController
 * handles validation server-side and returns CurrentUmbracoPage with
 * `TempData["AuthError"]` set on failure. The reload re-renders the login
 * modal with the error visible above the form.
 */

const MODAL_WRAPPER_PREFIX = 'amri-modal-';

// Legacy login has no reCAPTCHA (LoginModalTemplate.cshtml) — the login modal
// carries none. (Account creation keeps its reCAPTCHA via _RegisterModal.)

function getModalById(id: string): HTMLElement | null {
    return document.getElementById(MODAL_WRAPPER_PREFIX + id);
}

function closeAllModals(): void {
    document.querySelectorAll<HTMLElement>('.amri-modal-wrapper').forEach((m) => {
        m.style.display = 'none';
    });
    document.body.classList.remove('amri-modal-open');
}

export function openModal(id: string): void {
    closeAllModals();
    const modal = getModalById(id);
    if (!modal) {
        return;
    }

    modal.style.display = 'block';
    document.body.classList.add('amri-modal-open');

    const firstInput = modal.querySelector<HTMLInputElement>('input:not([type="hidden"])');
    if (firstInput) {
        firstInput.focus();
    }
}

function handleToggleClick(e: Event): void {
    const target = e.target as HTMLElement | null;
    if (!target) {
        return;
    }

    const toggleHost = target.closest<HTMLElement>('[data-modal-toggle]');
    if (toggleHost) {
        e.preventDefault();
        const id = toggleHost.dataset.modalToggle;
        if (id) {
            openModal(id);
        }

        return;
    }

    const closeHost = target.closest<HTMLElement>('[data-modal-control="close"]');
    if (closeHost) {
        e.preventDefault();
        closeAllModals();
    }
}

function handleEscKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
        closeAllModals();
    }
}

// The header renders a logout link only when the visitor is signed in, so its
// presence is a reliable client-side "authenticated" signal.
function isAuthenticated(): boolean {
    return document.querySelector('a[href*="accountsurface/logout"]') !== null;
}

function shouldAutoOpenLogin(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    // Never surface the login form to an already-signed-in visitor. A
    // `?showLoginModal=1` (bookmarked, stale link, or carried on a re-render)
    // must not pop the login modal over a live session — the auth guard wins
    // over every auto-open trigger below.
    if (isAuthenticated()) {
        return false;
    }

    const errorEl = document.querySelector<HTMLElement>('#amri-modal-login .amri-form-error');
    if (errorEl && errorEl.textContent && errorEl.textContent.trim().length > 0) {
        return true;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('showLoginModal') === '1') {
        return true;
    }

    // Legacy /register/?signup=true — the marketing landing renders a
    // "Sign in to Redeem / Continue" CTA and the ?signup=true query flag
    // means "open the login modal on arrival." Authenticated visitors are
    // intercepted server-side and 302'd to /profile/ (see Program.cs
    // middleware); this branch fires for anonymous visitors only.
    return params.get('signup') === 'true';
}

export function initAuthModals(): void {
    document.addEventListener('click', handleToggleClick);
    document.addEventListener('keydown', handleEscKey);

    if (shouldAutoOpenLogin()) {
        openModal('login');
    }
}
